import { useState, useRef, useEffect } from 'react';
import {
  Button,
  Typography,
  Paper,
  Box,
  Container,
  Stack,
  CircularProgress
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import Swal from 'sweetalert2';

export default function SignatureUploader({ width = 400, height = 200, onCapture }) {
  const [uploadedImage, setUploadedImage] = useState(null);
  const [isConverting, setIsConverting] = useState(false);
  const fileInputRef = useRef(null);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [canvasSize, setCanvasSize] = useState({ width, height });

  // Calculate canvas size based on container width
  useEffect(() => {
    const updateCanvasSize = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        // Maintain aspect ratio but use container width
        const calculatedHeight = (height / width) * containerWidth;
        setCanvasSize({
          width: containerWidth,
          height: calculatedHeight
        });
      }
    };

    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);
    return () => window.removeEventListener('resize', updateCanvasSize);
  }, [width, height]);

  // Initialize canvas on mount or size change
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    // Set canvas dimensions
    canvas.width = canvasSize.width;
    canvas.height = canvasSize.height;
    
    const ctx = canvas.getContext('2d', { alpha: false });
    if (ctx) {
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvasSize.width, canvasSize.height);
      
      // If there was already an image, redraw it with new dimensions
      if (uploadedImage) {
        const img = new Image();
        img.onload = () => {
          drawImageProperly(img, canvas, ctx);
        };
        img.src = uploadedImage;
      }
    }
  }, [canvasSize, uploadedImage]);

  // Helper function to draw image centered and scaled properly
  const drawImageProperly = (image, canvas, ctx) => {
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    const hRatio = canvas.width / image.width;
    const vRatio = canvas.height / image.height;
    const ratio = Math.min(hRatio, vRatio);
    
    const centerX = (canvas.width - image.width * ratio) / 2;
    const centerY = (canvas.height - image.height * ratio) / 2;
    
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(
      image,
      0, 0, image.width, image.height,
      centerX, centerY, image.width * ratio, image.height * ratio
    );
  };

  // Drag-and-drop event listeners
  useEffect(() => {
    const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };
    
    const handleDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        processFile(e.dataTransfer.files[0]);
      }
    };
    
    const dropArea = document.querySelector('.drop-area');
    if (dropArea) {
      dropArea.addEventListener('dragover', handleDragOver);
      dropArea.addEventListener('drop', handleDrop);
      return () => {
        dropArea.removeEventListener('dragover', handleDragOver);
        dropArea.removeEventListener('drop', handleDrop);
      };
    }
  }, []);

  const processFile = (file) => {
    if (!file) return;
    setIsConverting(true);
    
    if (file.size > 5 * 1024 * 1024) {
      Swal.fire({
        icon: 'error',
        title: 'File Too Large',
        text: 'Please select a file under 5MB.',
        confirmButtonColor: '#3085d6'
      });
      setIsConverting(false);
      return;
    }
    
    const image = new Image();
    image.onload = () => {
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d', { alpha: false });
      
      if (ctx && canvas) {
        drawImageProperly(image, canvas, ctx);
        setUploadedImage(canvas.toDataURL('image/png', 0.8));
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Canvas Error',
          text: 'Canvas not ready. Please try again.',
          confirmButtonColor: '#3085d6'
        });
      }
      
      setIsConverting(false);
      URL.revokeObjectURL(image.src);
    };
    
    image.onerror = () => {
      Swal.fire({
        icon: 'error',
        title: 'Image Error',
        text: 'Error loading image. Please try another file.',
        confirmButtonColor: '#3085d6'
      });
      setIsConverting(false);
      URL.revokeObjectURL(image.src);
    };
    
    image.src = URL.createObjectURL(file);
  };

  const handleFileUpload = (event) => {
    if (event.target.files && event.target.files.length > 0) {
      processFile(event.target.files[0]);
    }
  };

  const handleClear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (ctx) {
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    setUploadedImage(null);
    setIsConverting(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleCapture = () => {
    if (canvasRef.current && uploadedImage) {
      const data = canvasRef.current.toDataURL('image/png', 0.8);
      if (onCapture && typeof onCapture === 'function') {
        onCapture(data);
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Signature captured successfully',
          confirmButtonColor: '#3085d6',
          timer: 2000,
          timerProgressBar: true
        });
      }
    } else {
      Swal.fire({
        icon: 'warning',
        title: 'No Signature',
        text: 'Please upload a signature image first',
        confirmButtonColor: '#3085d6'
      });
    }
  };

  return (
    <Container ref={containerRef}
    >
      {/* Canvas Container (Always Rendered but conditionally displayed) */}
      <Box
        sx={{
          border: 1,
          borderColor: 'grey.300',
          borderRadius: 1,
          overflow: 'hidden',
          mb: 2,
          bgcolor: 'background.paper',
          display: uploadedImage || isConverting ? 'block' : 'none',
          width: '100%',
          height: 'auto'
        }}
      >
        <Box
          component="canvas"
          ref={canvasRef}
          sx={{
            border: '1px solid #ccc',
            borderRadius: 2,
            backgroundColor: '#fafafa',
            width: '100%',
            height: '300px',
            touchAction: 'none',
            display: 'block',
          }}
        />
      </Box>

      {/* Upload Area or Loading Spinner */}
      {isConverting ? (
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'grey.50',
            minHeight: height,
            width: '100%'
          }}
        >
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="subtitle1">Converting image...</Typography>
        </Paper>
      ) : !uploadedImage ? (
        <Paper
          variant="outlined"
          className="drop-area"
          sx={{
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            bgcolor: 'grey.50',
            borderStyle: 'dashed',
            borderWidth: 2,
            '&:hover': { bgcolor: 'grey.100' },
            minHeight: height,
            width: '100%'
          }}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={handleFileUpload}
          />
          <CloudUploadIcon sx={{ fontSize: 40, color: '#9e9e9e' }} />
          <Typography variant="subtitle1" sx={{ mt: 2 }}>
            Click to upload or drag & drop
          </Typography>
          <Typography variant="body2" color="text.secondary">
            JPG, PNG, or GIF (max. 5MB)
          </Typography>
        </Paper>
      ) : (
        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleClear}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveAltIcon />}
            onClick={handleCapture}
          >
            Capture
          </Button>
        </Stack>
      )}
    </Container>
  );
}